import { useCallback } from 'react'
import isEmpty from 'lodash/isEmpty'
import { THeavyLoadsSchema } from 'transfix-form/forms/CarrierLoadPreferences/components/HeavyLoadsModal/schema/HeavyLoadsSchema'
import { IUseCarrierLoadPreferencesSubmitter } from 'transfix-form/forms/CarrierLoadPreferences/types'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Maybe } from 'types/graphqlTypes'
import { useCarrierProfileQuery } from 'apps/carrier/components/Carrier/graphql/CarrierProfile'
import { TOAST_ERROR_MESSAGE } from '../../../constants'
import { useUpdateCarrierLoadingCapabilityMutation } from '../graphql/UpdateCarrierLoadingCapabilityMutation'

const TOAST_SUCCESS_MESSAGE = 'Heavy loads preference updated successfully'

export const useHeavyLoadsSubmitter = (
  carrierApplicationId: string,
  loadingCapabilityId: Maybe<string>
): IUseCarrierLoadPreferencesSubmitter<THeavyLoadsSchema> => {
  const { openToast } = useGlobalToast()

  const {
    loading: fetchCarrierProfileLoading,
    refetch: fetchCarrierProfile,
  } = useCarrierProfileQuery({
    skip: true,
    variables: {
      id: carrierApplicationId,
    },
  })
  const [
    updateLoadingCapability,
    { loading: updateLoadingCapabilityLoading },
  ] = useUpdateCarrierLoadingCapabilityMutation()

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: TOAST_ERROR_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const submitHandler = useCallback(
    async ({ isHeavyLoadCapable }: THeavyLoadsSchema) => {
      const { data, errors: gqlErrors } = await updateLoadingCapability({
        variables: {
          input: {
            carrierApplicationId,
            isHeavyLoadCapable,
          },
        },
      })
      const { updateCarrierLoadingCapability } = data ?? {}
      const { errors: clientErrors } = updateCarrierLoadingCapability ?? {}

      if (isEmpty(gqlErrors) && isEmpty(clientErrors)) {
        if (!loadingCapabilityId) {
          await fetchCarrierProfile()
        }
        openToast({ toastMessage: TOAST_SUCCESS_MESSAGE })
        return true
      }
      showErrorToast()
      return false
    },
    [
      carrierApplicationId,
      fetchCarrierProfile,
      loadingCapabilityId,
      openToast,
      showErrorToast,
      updateLoadingCapability,
    ]
  )

  return {
    loading: fetchCarrierProfileLoading || updateLoadingCapabilityLoading,
    submitHandler,
  }
}
